import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';

import { FieldInterventionBloc } from '@connect-services/client/blocs/field-intervention.bloc';
import { FieldInterventionEnum } from '@connect-services/client/models/panel.model';
import { FieldInterventionsService, MultiPolygon } from '@connect-services/client/sdk/api';
import { ContentLayoutService, LAYOUT_KEY } from '@connect-services/client/services/content-layout.service';
import { CreateFieldInterventionService } from '@connect-services/client/services/create-field-intervention.service';
import {
    FIELD_INTERVENTION_AREA_MASK_ID,
    MapMaskService,
} from '@connect-services/client/services/map/mask/map-mask.service';
import {
    MapModalSelectionModeEnum,
    SelectionService,
} from '@connect-services/client/services/map/selection/selection.service';
import { MapControlService } from '@connect-services/client/services/map-control.service';
import { MapDrawService } from '@connect-services/client/services/map-draw.service';
import { MapModal, MapModalService } from '@connect-services/client/services/map-modal.service';
import { PanelService } from '@connect-services/client/services/panel.service';
import { SelectZoneLayerService } from '@connect-services/client/services/select-zone-layer.service';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export enum FieldInterventionAreaSelectionContextEnum {
    CREATE_MODAL = 'CREATE_MODAL',
    DASHBOARD = 'DASHBOARD',
    EDIT_MODAL = 'EDIT_MODAL',
    PLANNING = 'PLANNING',
}

@Injectable({
    providedIn: 'root',
})
export class FieldInterventionAreaSelectionModeService {
    public readonly context = signal<FieldInterventionAreaSelectionContextEnum | null>(null);
    public readonly selectedArea = signal<MultiPolygon | null>(null);
    public readonly selectionConfirmed = new Subject<MultiPolygon>();

    private readonly contentLayoutService = inject(ContentLayoutService);
    private readonly createFieldInterventionService = inject(CreateFieldInterventionService);
    private readonly fieldInterventionBloc = inject(FieldInterventionBloc);
    private readonly fieldInterventionServiceSdk = inject(FieldInterventionsService);
    private readonly mapControlService = inject(MapControlService);
    private readonly mapDrawService = inject(MapDrawService);
    private readonly mapMaskService = inject(MapMaskService);
    private readonly mapModalService = inject(MapModalService);
    private readonly panelService = inject(PanelService);
    private readonly selectionService = inject(SelectionService);
    private readonly selectZoneLayerService = inject(SelectZoneLayerService);
    private readonly snackBarService = inject(SnackBarService);

    public constructor() {
        this.createFieldInterventionService.fieldInterventionAreaSelectionConfirmed.subscribe(() => {
            void this.handleFieldInterventionAreaSelectionConfirmed();
        });
    }

    public open(context: FieldInterventionAreaSelectionContextEnum): void {
        this.context.set(context);

        this.panelService.closePanel();
        this.contentLayoutService.changeContentSize(null);

        this.closeActiveMapModals();

        this.selectionService.resetSelectionState();
        this.selectionService.setModalMode(MapModalSelectionModeEnum.FIELD_INTERVENTION_AREA);
        this.selectionService.isSelectionActive.set(true);

        this.mapControlService.showSelectionModalControl();
        this.mapModalService.open(MapModal.SELECTION);
    }

    public async refreshFieldInterventionArea(
        fieldInterventionId: string,
        fieldInterventionArea: MultiPolygon,
    ): Promise<void> {
        await this.fieldInterventionBloc.load({ id: fieldInterventionId }, { refresh: true });

        this.mapMaskService.applyMask({
            area: fieldInterventionArea,
            id: FIELD_INTERVENTION_AREA_MASK_ID,
            showMask: true,
        });
    }

    private closeActiveMapModals(): void {
        this.mapModalService.close(MapModal.FILTER);
        this.mapModalService.close(MapModal.SELECTION);
    }

    private async closeSelectionMode(): Promise<void> {
        this.closeActiveMapModals();

        this.mapControlService.hideSelectionModalControl();

        this.mapDrawService.resetDraw();
        await this.selectZoneLayerService.removeLayers();

        this.selectionService.resetSelectionState();
    }

    private async handleFieldInterventionAreaSelectionConfirmed(): Promise<void> {
        const fieldInterventionArea = this.resolveFieldInterventionArea();
        if (!fieldInterventionArea) {
            return;
        }

        const context: FieldInterventionAreaSelectionContextEnum | null = this.context();

        this.selectedArea.set(fieldInterventionArea);

        if (
            context === FieldInterventionAreaSelectionContextEnum.CREATE_MODAL ||
            context === FieldInterventionAreaSelectionContextEnum.EDIT_MODAL
        ) {
            await this.closeSelectionMode();
            this.resetState();
            this.selectionConfirmed.next(fieldInterventionArea);

            return;
        }

        this.selectionConfirmed.next(fieldInterventionArea);
        await this.updateFieldInterventionArea(fieldInterventionArea);
    }

    private resetState(): void {
        this.context.set(null);
        this.selectedArea.set(null);
    }

    private resolveFieldInterventionArea(): MultiPolygon | null {
        if (this.mapDrawService.hasDrawnPolygon()) {
            return this.mapDrawService.getDrawnMultiPolygon();
        }

        return this.selectZoneLayerService.getSelectedMultiPolygon();
    }

    private restoreSourcePanel(): void {
        switch (this.context()) {
            case FieldInterventionAreaSelectionContextEnum.PLANNING:
                this.contentLayoutService.changeContentSize(
                    FieldInterventionEnum.FIELD_INTERVENTION_ENTITY_PLANNING,
                    LAYOUT_KEY.ONE_THIRD,
                );

                break;

            case FieldInterventionAreaSelectionContextEnum.DASHBOARD:
                this.contentLayoutService.changeContentSize(
                    FieldInterventionEnum.FIELD_INTERVENTION_DASHBOARD,
                    LAYOUT_KEY.ONE_THIRD,
                );

                break;

            case FieldInterventionAreaSelectionContextEnum.CREATE_MODAL:
            default:
                break;
        }
    }

    private async updateFieldInterventionArea(fieldInterventionArea: MultiPolygon): Promise<void> {
        const fieldIntervention = this.fieldInterventionBloc.value();
        if (!fieldIntervention) {
            return;
        }

        try {
            await firstValueFrom(
                this.fieldInterventionServiceSdk.fieldInterventionControllerPatch({
                    id: fieldIntervention.id,
                    updateFieldInterventionDto: {
                        fieldInterventionArea,
                    },
                }),
            );

            await this.refreshFieldInterventionArea(fieldIntervention.id, fieldInterventionArea);

            this.snackBarService.openSuccessSnackBar('fieldInterventionEditionSuccess');
        } catch {
            this.snackBarService.openErrorSnackBar('errorPATCH');
        } finally {
            await this.closeSelectionMode();
            this.restoreSourcePanel();
            this.resetState();
        }
    }
}
